import * as React from 'react'
import Button from '@mui/material/Button'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Alert, Box, Checkbox, Collapse, Dialog, DialogActions, DialogTitle, FormControl, FormControlLabel, IconButton, InputAdornment, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import { appdata, MaterialInfo, materialCategories, defaultMaterialInfo, Material } from '../internalModules'
import { alertStyle, boxStyle, dialogPaperProps, dialogTypographyStyle, halfWidthStyle, rowHeight, textFieldStyle } from '../Graphics'
import { CLFBaselines } from '../data/CLFBaselines'
import { TrackAnalyticsEvent } from '../analytics/analytics'
import { CreateMaterialEvent, EditMaterialEvent } from '../analytics/events'

type Props = {
  materialGuid: string,
  isOpen: boolean,
  closeDialog: Function
  acceptDialog: Function
  deleteMaterial?: Function
}

export default function UIMaterialDialog({materialGuid,
isOpen, closeDialog, acceptDialog, deleteMaterial = ()=>{}}: Props){

  const mode = materialGuid.length > 0 ? 'edit' : 'add'
  let otherMaterialNames = appdata.getMaterialNames()
  let initialMatInfo = defaultMaterialInfo
  let dialogTitle = 'Add New Material:'

  if (mode === 'edit'){
    initialMatInfo = appdata.getMaterial(materialGuid).info
    otherMaterialNames = appdata.getMaterialNames().filter( x => x !== initialMatInfo.name )
    dialogTitle = 'Edit Material:'
  }

  const [matInfo, setMatInfo] = React.useState(initialMatInfo)
  const [transparentChecked, setTransparentChecked] = React.useState(initialMatInfo.transparent)
  const [warnNameUnique, setWarnNameUnique] = React.useState(otherMaterialNames.includes(initialMatInfo.name))
  const [warnNameEmpty, setWarnNameEmpty] = React.useState(initialMatInfo.name === '')
  const [warnGwpZero, setWarnGwpZero] = React.useState(initialMatInfo.gwp === 0)
  const [warnDensityZero, setWarnDensityZero] = React.useState(initialMatInfo.density === 0 && initialMatInfo.units === 'kg')
  const [warnCategoryEmpty, setWarnCategoryEmpty] = React.useState(initialMatInfo.category === '')
  const [inputError, setInputError] = React.useState(inputHasError(initialMatInfo))

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    const value: string = (event.target as HTMLInputElement).value
    const newMatInfo = { ...matInfo, [propertyName]: value} as MaterialInfo
    setMatInfo(newMatInfo)
    if(propertyName === 'units'){
      if(value === 'kg' && matInfo.density <= 0){
        setWarnDensityZero(true)
      }
      if(value === 'kg' && matInfo.density > 0){
        setWarnDensityZero(false)
      }
      if(value !== 'kg'){
        setWarnDensityZero(false)
      }
    }
    if(propertyName === 'category'){
      if(value === ''){
        setWarnCategoryEmpty(true)
      }
      else{
        setWarnCategoryEmpty(false)
      }
    }
    setInputError(inputHasError(newMatInfo))
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement> ) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    const value: string = (event.target as HTMLInputElement).value
    const newMatInfo = { ...matInfo, [propertyName]: value} as MaterialInfo
    setMatInfo(newMatInfo)
    // Validate name, gwp, density
    if(propertyName === 'name'){
      if(otherMaterialNames.includes(value)){
        setWarnNameUnique(true)
      }
      if(!otherMaterialNames.includes(value)){
        setWarnNameUnique(false)
      }
      if(value.length === 0){
        setWarnNameEmpty(true)
      }
      if(value.length > 0){
        setWarnNameEmpty(false)
      }
    }
    if(propertyName === 'gwp'){
      if(Number(value) > 0){
        setWarnGwpZero(false)
      }
      else{
        setWarnGwpZero(true)
      }
    }
    if(propertyName === 'density'){
      if(Number(value) > 0){
        setWarnDensityZero(false)
      }
      if(Number(value) <= 0 && matInfo.units === 'kg'){
        setWarnDensityZero(true)
      }
    }
    setInputError(inputHasError(newMatInfo))
  }

  const handleClickOk = () => {
    acceptDialog(matInfo)
    if(mode === 'add'){
      TrackAnalyticsEvent(CreateMaterialEvent)
    }
    if(mode === 'edit'){
      TrackAnalyticsEvent(EditMaterialEvent)
    }
    closeDialog()
  }

  const handleClickCancel = () => {
    closeDialog()
  }

  const handleChangeCheckbox = () => {
    const checked = transparentChecked
    setMatInfo( { ...matInfo, transparent: !checked })
    setTransparentChecked(!checked)
  }

  function handleReset() {
    const clfInfo = CLFBaselines.find(x => x.guid === matInfo.guid)
    if(clfInfo){
      setMatInfo(clfInfo)
    }
  }

  function handleDelete() {
    console.log('delete clicked')
    deleteMaterial(matInfo.guid)
  }

  function inputHasError(info: MaterialInfo): boolean{
    let hasError = true
    if(info.name !== ''
      && !otherMaterialNames.includes(info.name)
      && info.category !== ''
      && info.gwp > 0
      ){
        if(info.units === 'kg'){
          if(info.density > 0){
            hasError = false
          }
        }
        else{
          hasError = false
        }
      }
    return hasError
  }

  function showDensityInput(){
    if (matInfo.units === 'kg') {
      return (
        <>
          <Typography sx={dialogTypographyStyle} color='secondary'>Density is required for GWP per kg:</Typography>
          <TextField
            name='density'
            value={matInfo.density}
            onChange={handleChange}
            style={textFieldStyle}
            type='number'
            InputProps={{
              startAdornment: <InputAdornment position="start">Density (lb/ft3):</InputAdornment>,
              style: textFieldStyle
            }}
          />
        </>
      )
    }
    else{
      return (<></>)
    }
  }

  function showWarnings(){
    return(
      <>
        <Collapse in={warnNameUnique}>
          <Alert severity={'error'} sx={alertStyle} >
            Material name must be unique.
          </Alert>
        </Collapse>
        <Collapse in={warnNameEmpty}>
          <Alert severity={'error'} sx={alertStyle} >
            Enter a material name.
          </Alert>
        </Collapse>
        <Collapse in={warnCategoryEmpty}>
          <Alert severity={'error'} sx={alertStyle} >
            Select a category.
          </Alert>
        </Collapse>
        <Collapse in={warnGwpZero}>
          <Alert severity={'error'} sx={alertStyle} >
            Global warming potential must be greater than zero.
          </Alert>
        </Collapse>
        <Collapse in={warnDensityZero}>
          <Alert severity={'error'} sx={alertStyle} >
            Density must be greater than zero when units are 'per kg'.
          </Alert>
        </Collapse>
      </>
    )
  }

  function showRevertCLF(){
    const clfMat = CLFBaselines.find(x => x.guid === matInfo.guid)
    if(clfMat && !Material.infoMatchesBaseline(matInfo)){
      return(
        <Button variant='outlined' onClick={handleReset} sx={{marginRight: 10, marginBottom: 2}}>
          Reset to CLF Data
        </Button>
      )
    }
    else{ return(<></>)}
  }

  function showDelete(){
    const clfMat = CLFBaselines.find(x => x.guid === matInfo.guid)
    if(clfMat === undefined && mode === 'edit'){
      return(
        <Button variant='text' color={'warning'} onClick={handleDelete}
          sx={{marginRight: '45%', marginBottom: 2}}
          startIcon={<DeleteForeverIcon/>}>
          Delete
        </Button>
      )
    }
    else{ return(<></>)}
  }

  return(
    <>
      <Dialog
        open={isOpen}
        onClose={handleClickCancel}
        maxWidth='sm'
        fullWidth
        scroll='body'
        PaperProps={dialogPaperProps}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title" fontSize={30} sx={{pl:4}}>
          {dialogTitle}
        </DialogTitle>
        <Box sx={boxStyle}>
          <Typography sx={dialogTypographyStyle} color='secondary'>Material Name:</Typography>
          <TextField autoComplete='off'
            name='name'
            value={matInfo.name}
            style={textFieldStyle}
            InputProps={{
              startAdornment: <InputAdornment position='start'>Name:</InputAdornment>,
              style: textFieldStyle
            }}
            onChange={handleChange}
          />
          <Typography sx={dialogTypographyStyle} color='secondary'>Material Category:</Typography>
          <FormControl fullWidth>
            <Select 
              name='category'
              value={matInfo.category}
              onChange={handleChangeSelect}
              style={{height: rowHeight}}
              displayEmpty
            >
              <MenuItem disabled value=''><em>Select category...</em></MenuItem>
              {materialCategories.map( i => (
                <MenuItem key={i} value={i}>{i}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={dialogTypographyStyle} color='secondary'>Global Warming Potential (kgCO2e) and units:</Typography>
          <FormControl sx={halfWidthStyle}>
            <TextField
              name='gwp'
              value={matInfo.gwp}
              style={textFieldStyle}
              type='number'
              InputProps={{
                startAdornment: <InputAdornment position='start'>kgCO2e:</InputAdornment>,
                style: textFieldStyle
              }}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl sx={halfWidthStyle}>
            <Select
              name='units'
              value={matInfo.units}
              onChange={handleChangeSelect}
              style={{height: rowHeight}}
            >
              <MenuItem key='1' value='m2'>per m2</MenuItem>
              <MenuItem key='2' value='m3'>per m3</MenuItem>
              <MenuItem key='3' value='kg'>per kg</MenuItem>
            </Select>
          </FormControl>
          {showDensityInput()}
          <Typography sx={dialogTypographyStyle} color='secondary'>Transparent material:</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={transparentChecked}
                onChange={handleChangeCheckbox}
                name="transparent"
                color="primary"
              />
            }
            label="Material is transparent (only affects 3d graphics)"
          />
          <Typography sx={dialogTypographyStyle} color='secondary'>Optional material notes:</Typography>
          <TextField fullWidth multiline
            minRows={2}
            maxRows={10}
            name='notes'
            value={matInfo.notes}
            onChange={handleChange}
            placeholder='enter optional notes, assumptions, url to EPDs, etc...'
          />
        </Box>

        {showWarnings()}

        <DialogActions>
          {showRevertCLF()}
          {/* {showDelete()} */}
          <Button variant='contained' onClick={handleClickOk} disabled={inputError} sx={{marginBottom: 2}}>
            Ok
          </Button>
          <Button variant='outlined' onClick={handleClickCancel} sx={{marginRight: 3, marginBottom: 2}}>
            Cancel
          </Button>
        </DialogActions>
        
      </Dialog>
    </>
  )

}